import Tooltip, { RichTooltipLabel } from '@components/Tooltip/Tooltip';
import { faLock, faUnlock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { analytics } from '@modules/analytics';
import { useCallback, useMemo } from 'react';

import styles from './CardHeaderPictures.module.scss';

const LockIcon = () => <FontAwesomeIcon icon={faLock} />;
const UnlockIcon = () => <FontAwesomeIcon icon={faUnlock} />;
const PictureLockButton: React.FC<
  React.PropsWithChildren<{
    locked: boolean | undefined;
    canLock: boolean | undefined;
    position: 'top-left' | 'top-right' | 'bottom-right';
    background?: boolean;
    hoverText?: string;
    onClick: React.MouseEventHandler;
  }>
> = ({ position, background, hoverText, onClick, locked, canLock }) => {
  const handleClick = useCallback<React.MouseEventHandler>(
    (e) => {
      analytics.track({ name: 'picturelockbutton_clicked' });
      onClick(e);
    },
    [onClick],
  );
  const ToolTipLabel = useMemo(() => {
    if (locked && canLock) {
      return (
        <RichTooltipLabel
          label="Enable editing"
          subtext="Cards that inherit from this template will be change this image."
        />
      );
    } else if (locked) {
      return <RichTooltipLabel label="Image Locked" subtext="This image cannot be edited" />;
    } else if (canLock) {
      return (
        <RichTooltipLabel
          label="Disable editing"
          subtext="Cards that inherit from this template will not be able to change this image."
        />
      );
    } else {
      return null;
    }
  }, [canLock, locked]);
  if (!canLock && !locked) return null; // don't render if no locks are possible
  return (
    <button
      className={styles['lock-btn']}
      disabled={!canLock}
      data-is-locked={!!locked}
      data-position={position}
      data-bg={background}
      data-has-text={!!hoverText}
      onClick={handleClick}
    >
      <Tooltip size="small" label={ToolTipLabel} aria-label="">
        <span>
          {locked ? <LockIcon /> : <UnlockIcon />}
          {hoverText !== undefined && <span className={styles['hover-text']}>{hoverText}</span>}
        </span>
      </Tooltip>
    </button>
  );
};

export default PictureLockButton;
